import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { lobbyDayApi, legislatorApi, lobbyAttendeeApi, lobbyAskTemplateApi, lobbyAttachmentApi } from '../lib/api'
import type {
  SaveLobbyDayEventRequest, SaveLobbyDayAskRequest, SaveOutreachRequest,
  SaveLegislatorRequest, SaveLobbyScheduleRequest, RecordOutcomeRequest,
  CreateFollowUpListRequest, OfficeLevel, SyncLegislatorsRequest,
  AttendeeDeliveryMode, SaveLogisticsRequest, CreateLegislatorNoteRequest,
  AttendanceStatus, MeetingRole,
  SaveAskTemplateRequest,
} from '../types/lobbyDay'
import type { RevokeCanvasserGrantRequest } from '../types/canvasserGrant'

// ---------- Events ----------

export function useLobbyDayEvents(status?: string) {
  return useQuery({
    queryKey: ['lobby-day', 'events', { status }],
    queryFn: async () => {
      const res = await lobbyDayApi.getEvents(status ? { status } : undefined)
      return res.data
    },
  })
}

export function useLobbyDayEvent(id: string | undefined) {
  return useQuery({
    queryKey: ['lobby-day', 'events', id],
    queryFn: async () => (await lobbyDayApi.getEvent(id!)).data,
    enabled: !!id,
  })
}

export function useCreateLobbyDayEvent() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: SaveLobbyDayEventRequest) => (await lobbyDayApi.createEvent(data)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lobby-day', 'events'] }),
  })
}

export function useUpdateLobbyDayEvent(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: SaveLobbyDayEventRequest) => (await lobbyDayApi.updateEvent(id, data)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lobby-day'] }),
  })
}

export function useDeleteLobbyDayEvent() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => { await lobbyDayApi.deleteEvent(id) },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lobby-day', 'events'] }),
  })
}

// ---------- Attendees ----------

export function useLobbyDayAttendees(eventId: string | undefined) {
  return useQuery({
    queryKey: ['lobby-day', 'attendees', eventId],
    queryFn: async () => (await lobbyDayApi.getAttendees(eventId!)).data,
    enabled: !!eventId,
  })
}

// Resolves to { added, tokens, attendees } (LW-24) -- `tokens` carries the show-once raw
// token per newly-minted attendee grant, `attendees` is the refreshed roster. Consumers that
// only care about the count/roster can ignore `tokens`; RosterView's show-once map is seeded
// from it.
export function useAddAttendees(eventId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (contactIds: string[]) => (await lobbyDayApi.addAttendees(eventId, contactIds)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lobby-day', 'attendees', eventId] })
      qc.invalidateQueries({ queryKey: ['lobby-day', 'schedule', eventId] })
      qc.invalidateQueries({ queryKey: ['lobby-day', 'events', eventId] })
      qc.invalidateQueries({ queryKey: ['lobby-day', 'targets', eventId] })
    },
  })
}

export function useRemoveAttendee(eventId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (attendeeId: string) => { await lobbyDayApi.removeAttendee(eventId, attendeeId) },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lobby-day', 'attendees', eventId] })
      qc.invalidateQueries({ queryKey: ['lobby-day', 'schedule', eventId] })
      qc.invalidateQueries({ queryKey: ['lobby-day', 'events', eventId] })
    },
  })
}

export function useRotateAttendeeToken(eventId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (attendeeId: string) => (await lobbyDayApi.rotateAttendeeToken(eventId, attendeeId)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lobby-day', 'attendees', eventId] }),
  })
}

export function useRevokeAttendeeToken(eventId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ attendeeId, body }: { attendeeId: string; body: RevokeCanvasserGrantRequest }) => {
      await lobbyDayApi.revokeAttendeeToken(eventId, attendeeId, body)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lobby-day', 'attendees', eventId] }),
  })
}

export function useSetDeliveryMode(eventId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ attendeeId, mode }: { attendeeId: string; mode: AttendeeDeliveryMode }) => {
      await lobbyDayApi.setDeliveryMode(eventId, attendeeId, mode)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lobby-day', 'attendees', eventId] }),
  })
}

export function useEventPrintPackets(eventId: string | undefined, modes?: AttendeeDeliveryMode[]) {
  return useQuery({
    queryKey: ['lobby-day', 'print-packets', eventId, modes ?? null],
    queryFn: async () => (await lobbyDayApi.getPrintPackets(eventId!, modes)).data,
    enabled: !!eventId,
  })
}

// ---------- Asks ----------

export function useSaveAsk(eventId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ askId, data }: { askId?: string; data: SaveLobbyDayAskRequest }) =>
      askId
        ? (await lobbyDayApi.updateAsk(eventId, askId, data)).data
        : (await lobbyDayApi.createAsk(eventId, data)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lobby-day', 'events', eventId] }),
  })
}

export function useDeleteAsk(eventId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (askId: string) => { await lobbyDayApi.deleteAsk(eventId, askId) },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lobby-day', 'events', eventId] }),
  })
}

// ---------- Ask templates (LW-10) ----------

export function useAskTemplates() {
  return useQuery({
    queryKey: ['lobby-day', 'ask-templates'],
    queryFn: async () => (await lobbyAskTemplateApi.list()).data,
  })
}

export function useSaveAskTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: SaveAskTemplateRequest) => (await lobbyAskTemplateApi.save(data)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lobby-day', 'ask-templates'] }),
  })
}

export function useRenameAskTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ templateId, name }: { templateId: string; name: string }) =>
      (await lobbyAskTemplateApi.rename(templateId, { name })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lobby-day', 'ask-templates'] }),
  })
}

export function useDeleteAskTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (templateId: string) => { await lobbyAskTemplateApi.delete(templateId) },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lobby-day', 'ask-templates'] }),
  })
}

export function useCreateAskFromTemplate(eventId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (templateId: string) =>
      (await lobbyDayApi.createAskFromTemplate(eventId, templateId)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lobby-day', 'events', eventId] }),
  })
}

// ---------- Bill linking (LegiScan) ----------

export function useBillSessions(eventId: string) {
  return useQuery({
    queryKey: ['lobby-day', 'bill-sessions', eventId],
    queryFn: () => lobbyDayApi.getBillSessions(eventId),
    staleTime: 60 * 60 * 1000,
  })
}

// ---------- Legislators ----------

export function useLegislators(
  params?: { chamber?: string; level?: OfficeLevel; search?: string; includeInactive?: boolean; state?: string },
) {
  return useQuery({
    queryKey: ['legislators', params ?? {}],
    queryFn: async () => (await legislatorApi.getAll(params)).data,
  })
}

export function useLegislator(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['legislators', id],
    queryFn: async () => (await legislatorApi.getById(id!)).data,
    enabled: enabled && !!id,
  })
}

export function useSaveLegislator() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id?: string; data: SaveLegislatorRequest }) =>
      id ? (await legislatorApi.update(id, data)).data : (await legislatorApi.create(data)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['legislators'] }),
  })
}

export function useDeleteLegislator() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => { await legislatorApi.delete(id) },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['legislators'] }),
  })
}

export function useImportLegislators() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (rows: SaveLegislatorRequest[]) => (await legislatorApi.import(rows)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['legislators'] }),
  })
}

// Enqueues a LegiScan sync job. Per frontend/src/CLAUDE.md's job-polling rule, this must NOT
// invalidate the ['legislators'] cache itself -- the polling consumer (LegislatorsPage, Task 9)
// invalidates inside the terminal job.status === 'Succeeded' branch instead, not at enqueue.
export function useSyncLegislators() {
  return useMutation({
    mutationFn: async (body: SyncLegislatorsRequest) => (await legislatorApi.sync(body)).data,
  })
}

// ---------- Legislator running notes (L13) ----------

export function useLegislatorNotes(legislatorId: string | undefined) {
  return useQuery({
    queryKey: ['legislators', legislatorId, 'notes'],
    queryFn: () => legislatorApi.getNotes(legislatorId!),
    enabled: !!legislatorId,
  })
}

export function useAddLegislatorNote(legislatorId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateLegislatorNoteRequest) => legislatorApi.addNote(legislatorId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['legislators', legislatorId, 'notes'] }),
  })
}

export function useDeleteLegislatorNote(legislatorId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (noteId: string) => legislatorApi.deleteNote(legislatorId, noteId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['legislators', legislatorId, 'notes'] }),
  })
}

// ---------- Outreach ----------

export function useOutreach(eventId: string | undefined) {
  return useQuery({
    queryKey: ['lobby-day', 'outreach', eventId],
    queryFn: async () => (await lobbyDayApi.getOutreach(eventId!)).data,
    enabled: !!eventId,
  })
}

export function useSaveOutreach(eventId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ legislatorId, data }: { legislatorId: string; data: SaveOutreachRequest }) => {
      await lobbyDayApi.saveOutreach(eventId, legislatorId, data)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lobby-day', 'outreach', eventId] }),
  })
}

// ---------- Logistics ----------

export function useLogistics(eventId: string | undefined) {
  return useQuery({
    queryKey: ['lobby-day', 'logistics', eventId],
    queryFn: async () => (await lobbyDayApi.getLogistics(eventId!)).data,
    enabled: !!eventId,
  })
}

export function useSaveLogistics(eventId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: SaveLogisticsRequest) => {
      await lobbyDayApi.saveLogistics(eventId, data)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lobby-day', 'logistics', eventId] }),
  })
}

// ---------- Schedule ----------

export function useLobbySchedule(eventId: string | undefined) {
  return useQuery({
    queryKey: ['lobby-day', 'schedule', eventId],
    queryFn: async () => (await lobbyDayApi.getSchedule(eventId!)).data,
    enabled: !!eventId,
  })
}

export function useSaveLobbySchedule(eventId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: SaveLobbyScheduleRequest) => (await lobbyDayApi.saveSchedule(eventId, data)).data,
    onSuccess: (schedule) => {
      qc.setQueryData(['lobby-day', 'schedule', eventId], schedule)
      qc.invalidateQueries({ queryKey: ['lobby-day', 'attendees', eventId] })
      qc.invalidateQueries({ queryKey: ['lobby-day', 'events', eventId] })
    },
  })
}

// ---------- Packets ----------

export function useMeetingPacket(meetingId: string | undefined) {
  return useQuery({
    queryKey: ['lobby-day', 'packet', meetingId],
    queryFn: async () => (await lobbyDayApi.getMeetingPacket(meetingId!)).data,
    enabled: !!meetingId,
  })
}

// ---------- Attendee mobile (anonymous) ----------

export function useAttendeeSchedule(token: string | undefined) {
  return useQuery({
    queryKey: ['lobby-attendee', 'schedule', token],
    queryFn: async () => (await lobbyAttendeeApi.getSchedule(token!)).data,
    enabled: !!token,
    staleTime: 5 * 60 * 1000,
  })
}

export function useAttendeePacket(token: string | undefined, meetingId: string | undefined) {
  return useQuery({
    queryKey: ['lobby-attendee', 'packet', token, meetingId],
    queryFn: async () => (await lobbyAttendeeApi.getPacket(token!, meetingId!)).data,
    enabled: !!token && !!meetingId,
    staleTime: Infinity,
    gcTime: 24 * 60 * 60 * 1000,
  })
}

// ---------- Debrief (ask-outcome capture) ----------

export function useEventDebrief(eventId: string | undefined) {
  return useQuery({
    queryKey: ['lobby-day', 'debrief', eventId],
    queryFn: async () => (await lobbyDayApi.getDebrief(eventId!)).data,
    enabled: !!eventId,
  })
}

export function useRecordMeetingOutcome(eventId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ meetingId, data }: { meetingId: string; data: RecordOutcomeRequest }) => {
      await lobbyDayApi.recordOutcome(meetingId, data)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lobby-day', 'debrief', eventId] }),
  })
}

export function useAttachMeetingAsk(eventId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ meetingId, askId }: { meetingId: string; askId: string | null }) =>
      lobbyDayApi.attachMeetingAsk(meetingId, { askId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lobby-day', 'debrief', eventId] })
      qc.invalidateQueries({ queryKey: ['lobby-day', 'reconciliation', eventId] })
      qc.invalidateQueries({ queryKey: ['lobby-day', 'events', eventId] })
    },
  })
}

// Fires the immediate day-of attendance PUT. Deliberately no onSuccess cache work here — the
// schedule editor hook owns optimism/reconciliation (it rides localMeetings and guards the
// cache patch against the hydration effect). See useLobbyScheduleEditor.setAttendance.
export function useSetAttendance() {
  return useMutation({
    mutationFn: ({ meetingId, assignmentId, status }:
      { meetingId: string; assignmentId: string; status: AttendanceStatus }) =>
      lobbyDayApi.setAttendance(meetingId, assignmentId, status).then((r) => r.data),
  })
}

// Day-of team lead (out-of-band, like attendance). The editor owns optimism; this only refreshes
// the board's lead roster (Call / Nudge read it) once the server has the new lead.
export function useSetAssignmentRole(eventId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ meetingId, assignmentId, role }:
      { meetingId: string; assignmentId: string; role: MeetingRole }) =>
      lobbyDayApi.setAssignmentRole(meetingId, assignmentId, role).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lobby-day', 'day-of-leads', eventId] }),
  })
}

// "I'm in this meeting" staff tag (self-only, idempotent). Both resolve to the meeting's staff
// list; the editor applies it out-of-band.
export function useTagMeStaff() {
  return useMutation({
    mutationFn: (meetingId: string) => lobbyDayApi.tagMeStaff(meetingId).then((r) => r.data),
  })
}

export function useUntagMeStaff() {
  return useMutation({
    mutationFn: (meetingId: string) => lobbyDayApi.untagMeStaff(meetingId).then((r) => r.data),
  })
}

// Team leads + phones for the day-of board (audited bulk read; phones never ride the schedule DTO).
export function useDayOfLeads(eventId: string) {
  return useQuery({
    queryKey: ['lobby-day', 'day-of-leads', eventId],
    queryFn: async () => (await lobbyDayApi.getDayOfLeads(eventId)).data,
    enabled: !!eventId,
  })
}

export function useCreateFollowUpList(eventId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: CreateFollowUpListRequest) =>
      (await lobbyDayApi.createFollowUpList(eventId, data)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lists'] }),
  })
}

// ---------- Packet PDF attachments (LD-T4-5) ----------

export function useEventAttachments(eventId: string | undefined) {
  return useQuery({
    queryKey: ['lobby-day', 'attachments', eventId],
    queryFn: async () => (await lobbyAttachmentApi.list(eventId!)).data,
    enabled: !!eventId,
  })
}

export function useUploadAttachment(eventId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (vars: { file: File; title?: string; askId?: string }) =>
      (await lobbyAttachmentApi.upload(eventId, vars.file, vars.title, vars.askId)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lobby-day', 'attachments', eventId] })
      qc.invalidateQueries({ queryKey: ['lobby-day', 'print-packets', eventId] })
    },
  })
}

export function useUpdateAttachment(eventId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (vars: { attachmentId: string; title: string; askId?: string | null }) =>
      (await lobbyAttachmentApi.update(eventId, vars.attachmentId, { title: vars.title, askId: vars.askId })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lobby-day', 'attachments', eventId] })
      qc.invalidateQueries({ queryKey: ['lobby-day', 'print-packets', eventId] })
    },
  })
}

export function useDeleteAttachment(eventId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (attachmentId: string) =>
      (await lobbyAttachmentApi.remove(eventId, attachmentId)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lobby-day', 'attachments', eventId] })
      qc.invalidateQueries({ queryKey: ['lobby-day', 'print-packets', eventId] })
    },
  })
}
